import { useAssetLoader } from "@/hooks/use-asset-loader";
import { AssetPreview } from "./asset-preview";
import { AssetRequestAccess } from "./asset-request-access";
import { Button } from "@/components/ui/button";
import { Minimize, Maximize } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface AssetsPublicViewProps {
  fullScreen?: boolean;
}

export const AssetsPublicView = ({ fullScreen = false }: AssetsPublicViewProps) => {
  const { asset, loading, assetId, error } = useAssetLoader({ requirePermission: false });
  const navigate = useNavigate();

  // Public view should only show a specific asset if assetId is provided
  // If no assetId, return empty (as public users shouldn't see a list of assets)
  if (!assetId) {
    return <div></div>;
  }

  // If loading, show a loading state
  if (loading) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p>Loading asset...</p>
        </div>
      </div>
    );
  }

  // If there's an error (like access denied), show request access page
  if (error) {
    return (
      <div className="container mx-auto py-8">
        <AssetRequestAccess
          assetId={assetId}
          assetName={error.assetName}
        />
      </div>
    );
  }

  // If there's an assetId and the asset is loaded, render the asset with the specific public layout
  if (asset && assetId) {
    // Full-screen mode
    if (fullScreen) {
      return (
        <div className="fixed inset-0 z-50 bg-background">
          <div className="absolute top-4 left-4 z-10">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/assets/${assetId}`)}
              className="flex items-center bg-background/80 backdrop-blur-sm"
            >
              <Minimize className="mr-2 h-4 w-4" />
              Exit Full Screen
            </Button>
          </div>
          <AssetPreview asset={asset} assetId={assetId} />
        </div>
      );
    }

    // Normal public view
    return (
        <div className="container mx-auto py-8 h-screen">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">{asset.name}</h1>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/assets/${assetId}/full`)}
              className="flex items-center"
            >
              <Maximize className="mr-2 h-4 w-4" />
              Full Screen
            </Button>
          </div>
          <div className="bg-white rounded-lg shadow p-6 flex h-full">
            <AssetPreview asset={asset} assetId={assetId} />
          </div>
        </div>
    );
  }

  // Fallback: return empty if no asset is loaded despite having assetId
  return <div></div>;
};
